// src/hooks/useClaudeStream.js
import { useCallback, useMemo, useRef, useState } from "react";

const API_BASE =
  (process.env.REACT_APP_CLAUDE_PROXY || "http://localhost:3001").replace(/\/+$/, "");

const DEFAULT_MODEL = "claude-3-haiku-20240307";

// Prix indicatifs (pour affichage uniquement)
const PRICE_BY_MODEL = {
  "claude-3-5-sonnet-20241022": { token_in: 0.003, token_out: 0.015 },
  "claude-3-5-haiku-20241022":  { token_in: 0.0008, token_out: 0.004 },
  "claude-3-haiku-20240307":    { token_in: 0.0008, token_out: 0.004 },
};

export function useClaudeStream() {
  const [text, setText] = useState("");
  const [chars, setChars] = useState(0);
  const [cost, setCost] = useState(0);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setErr] = useState(null);
  const controllerRef = useRef(null);
  const prices = PRICE_BY_MODEL[DEFAULT_MODEL];

  const cancel = useCallback(() => {
    try { controllerRef.current?.abort(); } catch {}
  }, []);

  // Parse SSE Anthropic (events "content_block_delta" avec delta.text)
  const _consumeSSE = async (response, onDelta, onDone) => {
    const reader = response.body?.getReader?.();
    if (!reader) throw new Error("Stream non supporté (body.getReader indisponible)");

    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Events séparés par double LF
      let idx;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const chunk = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);

        // lignes "data: {json}"
        const lines = chunk.split("\n").map((l) => l.trim());
        const dataLine = lines.find((l) => l.startsWith("data:"));
        if (!dataLine) continue;
        const payload = dataLine.slice(5).trim();
        if (payload === "[DONE]") {
          // fin propre
          break;
        }
        try {
          const event = JSON.parse(payload);
          // Les deltas texte arrivent ici
          if (
            event.type === "content_block_delta" &&
            event.delta?.type === "text_delta" &&
            typeof event.delta.text === "string"
          ) {
            onDelta(event.delta.text);
          }
          // usage final sur "message_delta" + "message_stop"
          if (event.type === "message_delta" && event.usage) {
            onDone(event.usage);
          }
        } catch {
          // ignore JSON partiel
        }
      }
    }
  };

  const ask = useCallback(
    async ({ message, max_tokens = 400, model = DEFAULT_MODEL, image = null }) => {
      setLoading(true);
      setErr(null);
      setText("");
      setChars(0);
      setCost(0);
      setUsage(null);

      const started = performance.now();
      const body = {
        message: String(message || ""),
        max_tokens,
        model,
        images: image ? [image] : [],
      };

      controllerRef.current = new AbortController();

      // 1) tentative streaming
      try {
        const r = await fetch(`${API_BASE}/api/claude/stream`, {
          method: "POST",
          mode: "cors",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          signal: controllerRef.current.signal,
        });

        if (!r.ok) {
          const t = await r.text();
          throw new Error(`Proxy stream ${r.status}: ${t.slice(0, 200)}`);
        }

        let acc = "";
        await _consumeSSE(
          r,
          (delta) => {
            acc += delta;
            setText(acc);
            setChars(acc.length);
          },
          (u) => {
            if (u) setUsage(u);
          }
        );
        const ms = Math.round(performance.now() - started);
        console.log(`[Claude][stream] Réponse en ${ms} ms`);
        setLoading(false);
        return acc;
      } catch (e) {
        // 2) repli non-stream
        console.warn(`[Claude] Stream indisponible (${e?.message || e}). Repli /api/claude`);
        try {
          const r2 = await fetch(`${API_BASE}/api/claude`, {
            method: "POST",
            mode: "cors",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
            signal: controllerRef.current.signal,
          });
          if (!r2.ok) {
            const t = await r2.text();
            throw new Error(`Proxy ${r2.status}: ${t.slice(0, 200)}`);
          }
          const json = await r2.json();
          const out = String(json.text || "");
          setText(out);
          setChars(out.length);
          setUsage(json.usage || null);
          const ms = Math.round(performance.now() - started);
          console.log(`[Claude] Réponse en ${ms} ms, images: ${body.images?.length || 0}`);
          setLoading(false);
          return out;
        } catch (e2) {
          setLoading(false);
          setErr(e2);
          throw e2;
        }
      }
    },
    []
  );

  // coût final (si usage disponible)
  const finalCost = useMemo(() => {
    if (!usage || !prices) return 0;
    // usage.{input_tokens, output_tokens} de l’API Anthropic
    const cin = (usage.input_tokens || 0) / 1000;
    const cout = (usage.output_tokens || 0) / 1000;
    return +(cin * prices.token_in + cout * prices.token_out).toFixed(6);
  }, [usage, prices]);

  // met à jour cost en fin
  if (finalCost && finalCost !== cost) {
    // setCost dans un microtask pour éviter setState en render
    queueMicrotask(() => setCost(finalCost));
  }

  return {
    text,
    chars,
    cost,
    usage,
    loading,
    error,
    ask,
    cancel,
    prices,
  };
}
